import { STONE_MODE_READ_ROLES } from '../../../../features/stoneModes/access';
import { ROUTE_ROLES, hasAnyAllowedRole } from '../../../../utils/routeRoles';
import { isGrowthOnly } from '../../../../utils/routeAccess';
import type { Role } from '../../../../generated/graphql';

const STONE_MODES_PATH = '/tools/stone-modes';

export const ALL_NAV_PATHS = [
  '/dashboard',
  '/analytics',
  '/users',
  '/crm',
  '/operations',
  '/programs',
  '/tools',
  STONE_MODES_PATH,
  '/settings',
] as const;

const BASE_VISIBLE_PATHS: Record<string, readonly string[]> = {
  admin: ALL_NAV_PATHS,
  // No /crm or /operations: their guard is AdminOrOperationsRoute (YOJO-3387 [nurses see a CRM
  // sidebar entry that bounces]). The ROUTE_ROLES filter below is the safety net if a list drifts.
  nurse: ['/dashboard', '/analytics', '/users'],
  developer: ['/dashboard', '/tools'],
  analyst: ['/dashboard', '/analytics'],
  content: ['/dashboard', '/analytics', '/programs', '/tools'],
  operations: ['/dashboard', '/analytics', '/crm', '/operations', '/tools'],
  // YOJO-3371 [care-portal CRM page for partner enquiries]: sales works CRM → Partner
  // Enquiries only (the CRM page and routes narrow it to that tab). Present so a sales-only
  // user does not fall through to DEFAULT_VISIBLE_PATHS.
  sales: ['/crm'],
  // No growth entry: growth opens no page (YOJO-3614 [growth staff role + growth MCP door]); a
  // growth-only user sees no entry at all (isNavItemVisible), and next to another role growth adds
  // nothing.
};

/**
 * Plus /tools/stone-modes for whoever may open it (STONE_MODE_READ_ROLES, YOJO-3259) — for a nurse that is the
 * one Tools page they see, without the Tools section itself.
 */
const ROLE_VISIBLE_PATHS: Record<string, readonly string[]> = Object.fromEntries(
  Object.entries(BASE_VISIBLE_PATHS).map(([role, paths]) => [
    role,
    (STONE_MODE_READ_ROLES as string[]).includes(role) && !paths.includes(STONE_MODES_PATH)
      ? [...paths, STONE_MODES_PATH]
      : paths,
  ]),
);

const DEFAULT_VISIBLE_PATHS: readonly string[] = ['/dashboard', '/analytics', '/users'];

/**
 * Determines whether a navigation item should be visible for a given set of roles.
 * Uses union semantics: a user sees everything that ANY of their roles grants, intersected with
 * what the path's route guard admits (ROUTE_ROLES) — YOJO-3387 [nurses see a CRM sidebar entry
 * that bounces]. A path whose parent route has no guard gets no extra filter.
 */
export function isNavItemVisible(roles: string[], href: string): boolean {
  // Growth works through MCP and sees no member or customer data in the portal (YOJO-3614)
  if (isGrowthOnly(roles as Role[])) return false;

  const visiblePaths = new Set<string>();

  let hasNavRole = false;
  for (const role of roles) {
    const paths = ROLE_VISIBLE_PATHS[role];
    if (paths) {
      hasNavRole = true;
      for (const p of paths) visiblePaths.add(p);
    }
  }

  if (!hasNavRole) {
    for (const p of DEFAULT_VISIBLE_PATHS) visiblePaths.add(p);
  }

  if (!visiblePaths.has(href)) return false;

  const guardRoles = ROUTE_ROLES[href];
  return guardRoles === undefined || hasAnyAllowedRole(roles, guardRoles);
}
